<template>
  <div class="loyalty-section">
    <div v-if="!error && (!profile.firstName || rewardsSummary === null)" class="loader-loyalty-header-status grid-x">
      <Skeleton height="200px" />
    </div>
    <template v-else-if="hasStarRewardsSummary()">
      <div
        v-if="profile.firstName"
        class="grid-x large-align-middle large-padding-left-m medium-padding-m large-padding-right-m small-padding-xs loyalty-container-relative"
        :class="['loyalty-' + (!currentTier ? 'notier' : currentTier), { 'has-flourishes': hasFlourishBanner }]"
      >
        <LoyaltyCelebrationFlourishes v-if="brand === 'mcom'" />
        <div class="cell large-6 medium-4 small-6 text-left loyalty-header-status grid-y">
          <div class="loyalty-greetings cell small-9">
            <div class="eyebrow">hello,</div>
            <div class="h1 firstname" data-fs-mask>
              {{ profile.firstName }}
            </div>
          </div>
          <template v-if="currentTier">
            <div class="loyalty-tier-status">
              <div :class="['loyalty-title-' + currentTier]" class="loyalty-tier-text h3" data-fs-mask>
                {{ currentTier }}
              </div>
            </div>
          </template>
          <template v-if="!emailLookupMembershipExists && !currentTier">
            <div class="join-star-rewards">
              <div class="join-copy large">Join Star Rewards and get points, perks & more.</div>
            </div>
          </template>
          <div class="title"><span class="star-rewards-red-small"></span> STAR REWARDS</div>
        </div>
        <div class="cell large-6 medium-8 small-12 text-right spinner-area">
          <button class="inbox-wrapper hide-for-large tooltip-wrapper">
            <Inbox :icon-size="'huge'" />
            <div class="css-tooltip caret-top" :class="showTooltip ? 'open' : ''">Inbox</div>
          </button>
          <LoyaltyHeaderSpinner />
          <template v-if="currentTier !== ''">
            <div
              v-if="currentTier && totalStarRewardCardsBalance && totalStarRewardCardsBalance !== '0.00'"
              class="star-rewards-balance-copy medium medium-margin-top-xs large-margin-top-xs"
              data-fs-mask
            >
              You have ${{ rewardsInfo?.rewardMoney }} in Star Money expiring on {{ rewardsInfo?.expiryDate }}
            </div>
            <div v-if="totalStarRewardCardsBalance === '0.00' || (!totalStarRewardCardsBalance && currentTier)">
              <template v-if="currentTier === 'platinum'">
                <div class="small-8 star-rewards-balance-copy medium medium-margin-top-xs large-margin-top-xs" data-fs-mask>
                  You're {{ currentTier }} through {{ tierExpiryDate }}
                </div>
              </template>
              <template v-else>
                <div class="star-rewards-balance-copy medium margin-top-xs medium-margin-top-xs large-margin-top-xs" data-fs-mask>
                  You're {{ pointsToNextAward || 1000 }} points away from your next reward
                </div>
              </template>
            </div>
          </template>
          <template v-else>
            <div class="star-rewards-balance-copy medium join-star-rewards medium-margin-top-xs large-margin-top-xs">
              <div class="join-copy medium">Join Star Rewards and get points, perks & more</div>
            </div>
          </template>
        </div>
        <template v-if="!emailLookupMembershipExists && !currentTier">
          <div class="learn-more large-12 text-right small-12 join-free">
            <a class="link-sm" href="/loyalty/starrewards?lid=star_rewards-star_rewards&linklocation=pagecontent">Join for Free</a>
          </div>
        </template>
        <template v-else>
          <div class="learn-more large-12 text-right small-12">
            <a
              class="link-sm"
              href="/loyalty/starrewards?cm_sp=macys_account-_-starrewards-_-star_rewards&lid=star_rewards-star_rewards&linklocation=pagecontent"
            >
              Learn more</a
            >
          </div>
        </template>
      </div>
    </template>
    <template v-else-if="error && error.length">
      <Notification severity="error">
        <span v-html="error"></span>
      </Notification>
    </template>
  </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { Cookie } from '@vue-util/cookie';
import Skeleton from '@atomic-ui/skeleton/Skeleton';
import Notification from '@atomic-ui/notification/Notification';
import { useProfileStore } from '@/stores/useProfileStore';
import { useDashboardStore } from '@/stores/useDashboardStore';
import Inbox from '../inbox/Inbox.vue';
import LoyaltyHeaderSpinner from './LoyaltyHeaderSpinner.vue';
import { brand } from 'config';
import LoyaltyCelebrationFlourishes from './LoyaltyCelebrationFlourishes.vue';
import { getFlourishBannerFlags } from '@/utils/loyalty';

const profileStore = useProfileStore();
const dashboardStore = useDashboardStore();

const { profile } = storeToRefs(profileStore);
const { rewardsSummary, starRewardsBanners } = storeToRefs(dashboardStore);
const showTooltip = ref(false);

const error = ref(null);

const hasFlourishBanner = computed(() => {
  if (brand !== 'mcom') return false;
  return getFlourishBannerFlags(starRewardsBanners.value).isNovember;
});

const currentTier = computed(() => (rewardsSummary.value?.tierInfo?.tierName || '').toLowerCase());

const emailLookupMembershipExists = computed(() => rewardsSummary.value?.emailLookupMembershipExists);

const totalStarRewardCardsBalance = computed(() => rewardsSummary.value?.rewardsInfo?.totalStarRewardCardsBalance);

const rewardsInfo = computed(() => rewardsSummary.value?.rewardsInfo?.rewardCards?.[0]);

const pointsToNextAward = computed(() => rewardsSummary.value?.rewardsInfo?.pointsToNextAward);

const yearToDateSpend = computed(() => rewardsSummary.value?.tierInfo?.yearToDateSpend);

const tierExpiryDate = computed(() => {
  let expirydate = rewardsSummary.value?.tierInfo?.tierExpiryDate;

  // For platinum users only, calculate expiration date based on spend threshold for the year
  // For platinum users who have met $1200 net spend threshold: use following year for expiration date
  // For platinum users who have NOT met $1200 net spend threshold: use current year for expiration date
  if (parseInt(yearToDateSpend.value, 10) >= 1200 && currentTier.value === 'platinum') {
    expirydate = new Date(new Date().getFullYear() + 1, 11, 31);
  } else if (parseInt(yearToDateSpend.value, 10) < 1200 && currentTier.value === 'platinum') {
    expirydate = new Date(new Date().getFullYear(), 11, 31);
  }
  return new Date(expirydate).toLocaleDateString();
});

const hasStarRewardsSummary = () => {
  // assigin data if summary exists
  const isError = rewardsSummary.value?.errors?.error;
  if (isError && isError.length) {
    error.value = isError[0].message;
    return false;
  }
  if (rewardsSummary.value !== null) {
    if (rewardsSummary.value?.tierInfo?.tierName) {
      return true;
    }
    if (!emailLookupMembershipExists.value) {
      return true;
    }
  }
  return false;
};

onMounted(() => {
  const userId = Cookie.get('macys_online_uid');
  Promise.all([dashboardStore.fetchRewardsSummary(), profileStore.fetchUserProfile(userId)]);
});
</script>
<style lang="scss" brand="mcom" scoped>
@import '@csgIcons/_star-rewards-red-small.scss';

.loyalty-tier-text {
  margin-top: rem-calc(3);
  text-transform: capitalize;
}

.loyalty-section {
  margin-right: rem-calc(20);
  .firstname {
    @include breakpoint(medium up) {
      position: relative;
      width: rem-calc(500);
    }
    @include breakpoint(small down) {
      width: rem-calc(320);
      font-size: $h2-font-size;
    }
  }
  @include breakpoint(large up) {
    margin-right: rem-calc(0);
  }
  .loyalty-container-relative {
    position: relative;
    overflow: hidden;

    &.has-flourishes {
      @include breakpoint(medium up) {
        height: auto;
        min-height: rem-calc(227);
      }

      .loyalty-header-status {
        .join-star-rewards {
          @include breakpoint(medium only) {
            width: 100%;
            max-width: rem-calc(200);
          }
          @include breakpoint(large up) {
            width: 100%;
            @media (max-width: 1399.98px) {
              max-width: rem-calc(260);
            }
            @media (max-width: 1200px) {
              max-width: rem-calc(200);
            }
            @media (min-width: 1400px) {
              max-width: rem-calc(280);
            }
          }
          .join-copy {
            white-space: normal;
            word-break: normal;
            @include breakpoint(medium up) {
              margin-bottom: rem-calc(8);
            }
          }
        }

        .title {
          @include breakpoint(medium up) {
            margin-top: 0;
            white-space: nowrap;
          }
        }
      }

      .spinner-area {
        @include breakpoint(medium up) {
          height: auto;
          min-height: rem-calc(130);
        }

        .star-rewards-balance-copy {
          @include breakpoint(large up) {
            @media (max-width: 1399.98px) {
              max-width: rem-calc(280);
              margin-left: auto;
              white-space: normal;
            }
            @media (max-width: 1200px) {
              max-width: rem-calc(200);
            }
            @media (max-width: 1100px) {
              max-width: rem-calc(180);
            }
            @media (min-width: 1400px) {
              max-width: none;
              white-space: nowrap;
            }
          }
        }
      }

      .learn-more {
        @include breakpoint(medium up) {
          margin-top: rem-calc(8);
        }
      }
    }
  }
  .loyalty-bronze,
  .loyalty-gold,
  .loyalty-silver,
  .loyalty-platinum,
  .loyalty-notier {
    position: relative;
    overflow: hidden;
    height: rem-calc(227);
    background-color: $background-color;
    @include breakpoint(small down) {
      min-height: rem-calc(258);
    }
  }
  .loyalty-title-bronze,
  .loyalty-title-silver,
  .loyalty-title-gold,
  .loyalty-title-platinum {
    color: $black;
    text-transform: capitalize;
  }
  .learn-more {
    position: relative;
    z-index: 2;
    @include breakpoint(small down) {
      position: relative;
      bottom: rem-calc(120);
    }
  }
  .spinner-area {
    position: relative;
    z-index: 2;
    height: rem-calc(130);
    .join-copy {
      display: none;
    }
    @include breakpoint(small down) {
      position: relative;
      bottom: rem-calc(165);
      display: flex;
      flex-direction: row-reverse;
      justify-content: center;
      height: rem-calc(96);
      .join-copy {
        display: block;
      }
    }

    div.star-rewards-balance-copy {
      @include breakpoint(small down) {
        width: rem-calc(164);
        height: rem-calc(96);
        box-sizing: border-box;
        padding: rem-calc(8 32);
        border-right: thin $gray-3-color solid;
      }
    }
    .inbox-wrapper {
      position: absolute;
      left: 92%;
      @include breakpoint(small only) {
        bottom: rem-calc(135);
      }
    }
  }

  .loyalty-header-status {
    position: relative;
    z-index: 2;
    @include breakpoint(small down) {
      height: rem-calc(230);
    }
    .loyalty-greetings {
      @include breakpoint(medium up) {
        height: auto;
      }
    }

    .join-star-rewards {
      @include breakpoint(medium up) {
        width: rem-calc(420);
        position: relative;
      }
      .join-copy {
        @include breakpoint(small down) {
          display: none;
        }
        margin-bottom: rem-calc(13.6);
      }
    }

    .join-star-rewards + .title {
      @include breakpoint(small down) {
        margin-top: rem-calc(40);
      }
    }
  }
}
</style>
<style lang="scss" scoped>
@import '@core/csg/src/scss/components/tooltip';
@include csg-tooltip;
.css-tooltip.caret-top {
  left: 50%;
  margin-top: 4px;
  transform: translateX(-50%);
}
.tooltip-wrapper {
  margin-top: 4px;
  cursor: pointer;
}
.css-tooltip {
  display: none;
}
.tooltip-wrapper:hover .css-tooltip,
.tooltip-wrapper:focus-visible .css-tooltip {
  display: block;
}
</style>
